import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Bell, CheckCheck } from "lucide-react";

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems((its) => its.map((i) => ({ ...i, read: true })));
  };

  const click = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell-btn" className="relative flex h-9 w-9 items-center justify-center rounded-full border border-[#1E293F] bg-[#141B2D] text-[#94A3B8] hover:text-white">
          <Bell size={17} />
          {unread > 0 && (
            <span data-testid="notif-unread-count" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF2A55] px-1 text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 border-[#1E293F] bg-[#0F1424] p-0 text-white">
        <div className="flex items-center justify-between border-b border-[#1E293F] px-3 py-2">
          <span className="text-sm font-bold">Notifications</span>
          {unread > 0 && (
            <button data-testid="notif-mark-read" onClick={markAll} className="flex items-center gap-1 text-xs text-[#00F0FF] hover:underline">
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[#64748B]">No notifications yet.</div>
          ) : items.map((n) => (
            <button
              key={n.id}
              data-testid={`notif-item-${n.id}`}
              onClick={() => click(n)}
              className={`block w-full border-b border-[#1E293F] px-3 py-3 text-left transition-colors hover:bg-[#1A233A] ${n.read ? "opacity-60" : ""}`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#00F0FF]" />}
                {n.title}
              </div>
              <p className="mt-0.5 text-xs text-[#94A3B8]">{n.body}</p>
              <p className="mt-1 text-[10px] text-[#64748B]">{new Date(n.created_at).toLocaleString()}</p>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
